import { Link } from 'react-router-dom';
import { useStateContext } from '../../hooks/useStateContext';
import useSEO from '../../hooks/useSEO';
import { schemaGraph } from '../../utils/schema';
import ArizonaHowToRenewGuide from '../resources/ArizonaHowToRenewGuide';
import KansasHowToRenewGuide from '../resources/KansasHowToRenewGuide';

// Arizona has a full step-by-step guide at this URL (Guide Publishing Handoff, Week 1);
// other states keep the shorter state-driven page below. Separate components so React
// never swaps hook order when navigating between /arizona and /kansas how-to-renew.
export default function StateHowToRenewPage() {
  const { state } = useStateContext();
  if (state.slug === 'arizona') return <ArizonaHowToRenewGuide />;
  if (state.slug === 'kansas') return <KansasHowToRenewGuide />;
  return <GenericHowToRenewPage state={state} />;
}

function GenericHowToRenewPage({ state }) {
  // Some states (Kansas) have the school report completions to the regulator;
  // others (Arizona) have the student submit certificates themselves.
  const reports = state.reportsCompletions;

  useSEO({
    title: `How to Renew Your ${state.name} Real Estate License | Steady Education`,
    description: `Step-by-step guide to renewing your ${state.name} real estate license: ${state.requiredHours} CE hours, ${state.regulator} requirements, timeline, and fees.`,
    url: `https://steadyeducation.com/${state.slug}/how-to-renew`,
    jsonLd: schemaGraph({
      breadcrumb: [
        { name: 'Home', url: 'https://steadyeducation.com' },
        { name: state.name, url: `https://steadyeducation.com/${state.slug}` },
        { name: 'How to Renew', url: `https://steadyeducation.com/${state.slug}/how-to-renew` },
      ],
    }),
  });

  return (
    <div className="page-wrapper">
      <div className="container" style={{ maxWidth: 820 }}>
        <div style={styles.header}>
          <h1 className="page-title">How to Renew Your {state.name} Real Estate License</h1>
          <p className="page-subtitle">
            Everything you need to complete your {state.cycleYears}-year renewal — {state.regulator} requirements,
            {' '}the fastest path, and where students trip up.
          </p>
        </div>

        <section style={styles.section}>
          <h2 style={styles.h2}>What {state.name} requires</h2>
          <ul style={styles.list}>
            <li><strong>{state.requiredHours} hours</strong> of {state.regulator}-approved continuing education every {state.cycleYears} years</li>
            {state.coreHours && <li><strong>{state.coreHours} of those {state.requiredHours} hours</strong> must be the mandatory Required Core — the rest are electives</li>}
            {state.dailyHourCap && <li><strong>Max {state.dailyHourCap} CE hours per 24-hour period</strong> — plan for at least {Math.ceil(state.requiredHours / state.dailyHourCap)} days</li>}
            {state.onTimeFee && <li>Renewal fee: <strong>${state.onTimeFee}</strong> on time, <strong>${state.lateFee}</strong> if you miss the deadline</li>}
            {reports
              ? <li><strong>Steady Education reports your completion directly to {state.regulator}</strong> — you don't submit anything yourself</li>
              : <li>Certificates are your responsibility to submit to {state.regulator} — your school does not report them</li>}
          </ul>
        </section>

        <section style={styles.section}>
          <h2 style={styles.h2}>{reports ? 'The renewal process' : 'The 4-step renewal timeline'}</h2>
          {reports ? (
            <ol style={styles.list}>
              <li><strong>Before your expiration date:</strong> complete all {state.requiredHours} {state.regulator}-approved CE hours</li>
              <li><strong>Pass each course</strong> — {state.regulator} requires at least 90% on the quiz and exam questions of every online course</li>
              <li><strong>We report your completion to {state.regulator} for you</strong>, within {state.reportingWindow}{state.reportingLimit && ` (${state.regulator}'s rule allows up to ${state.reportingLimit})`} — nothing for you to submit</li>
              <li><strong>Renew online</strong> with {state.regulator} and pay your renewal fee</li>
            </ol>
          ) : (
            <ol style={styles.list}>
              <li><strong>90 days before expiration:</strong> Check your {state.regulator} portal for category deficiencies</li>
              <li><strong>60 days before:</strong> Enroll and start your CE — don't wait</li>
              <li><strong>30 days before:</strong> Complete every hour, submit certificates via {state.regulator} portal</li>
              <li><strong>Renewal month:</strong> Pay the renewal fee online, done</li>
            </ol>
          )}
        </section>

        <section style={styles.section}>
          <h2 style={styles.h2}>Fastest legal path</h2>
          <p>The fastest way to satisfy every {state.regulator} category:</p>
          <ol style={styles.list}>
            <li>Buy the <Link to={`/${state.slug}/courses`} style={styles.inlineLink}>Complete {state.name} CE Package</Link> — ${state.packagePrice} covers every mandatory hour</li>
            <li>Complete each course online at your own pace ({state.dailyHourCap ? `up to ${state.dailyHourCap} hours/day` : 'any pace'})</li>
            {reports ? (
              <li>Pass, then <strong>we report your completion to {state.regulator}</strong> within {state.reportingWindow} — no certificates to file</li>
            ) : (
              <>
                <li>Download your certificates instantly on passing</li>
                <li>Submit each certificate through your {state.regulator} portal</li>
              </>
            )}
            <li>Renew your license online</li>
          </ol>
        </section>

        <div style={styles.cta}>
          <div>
            <strong>Ready to knock out your renewal?</strong>
            <p style={{ fontSize: 14, color: 'var(--color-text-muted)', margin: '4px 0 0' }}>
              All {state.requiredHours} hours, {state.regulator}-approved, ${state.packagePrice}. {reports ? `We report to ${state.regulator} for you.` : 'Instant certificates.'}
            </p>
          </div>
          <Link to={`/${state.slug}/courses`} className="btn btn-primary">See Courses →</Link>
        </div>

        {state.hasGuides && (
          <p style={{ marginTop: 32, fontSize: 15 }}>
            Want more detail? <Link to={`/${state.slug}/resources`} style={styles.inlineLink}>Browse our {state.name} CE guides →</Link>
          </p>
        )}
        {!state.hasGuides && state.pillarGuide && (
          <p style={{ marginTop: 32, fontSize: 15 }}>
            Want more detail? <Link to={state.pillarGuide} style={styles.inlineLink}>Read the full {state.name} CE requirements guide →</Link>
          </p>
        )}
      </div>
    </div>
  );
}

const styles = {
  header: { textAlign: 'center', marginBottom: 40 },
  section: { marginBottom: 32, background: 'white', border: '1px solid var(--color-border)', borderRadius: 12, padding: '24px 28px' },
  h2: { fontFamily: 'var(--font-display)', fontSize: 22, color: 'var(--slate-900)', marginBottom: 14 },
  list: { margin: '0 0 0 22px', padding: 0, lineHeight: 1.8, fontSize: 15, color: 'var(--slate-700)' },
  inlineLink: { color: 'var(--color-primary)', textDecoration: 'underline', fontWeight: 600 },
  cta: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 16, flexWrap: 'wrap', background: 'var(--tan-50)', border: '1px solid #b39e89', borderRadius: 14, padding: '20px 24px', marginTop: 24 },
};
